import { useState } from 'react';
import {
  useCashRegisterStatus,
  useCashRegisters,
  useCashRegister,
  useOpenCashRegister,
  useCloseCashRegister,
  useReopenCashRegister,
  useProfitHistory,
  useCashWithdrawals,
  useCreateCashWithdrawal,
  useDeleteCashWithdrawal,
} from '@/hooks/useCashRegister';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/context/authStore';
import { cierreZFiscal, reporteXFiscal } from '@/utils/fiscal';
import { fiscalZService } from '@/services/fiscalZ';
import { CashWithdrawalCategory, PaymentMethod, CloseType, WITHDRAWAL_CATEGORY_LABELS, PAYMENT_METHOD_LABELS } from '@/services/cashRegister';
import { formatPrice, formatDate, formatDateTime, parseARNumber } from '@/utils/format';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import CurrencyInput from '@/components/ui/CurrencyInput';
import {
  DollarSign,
  Lock,
  Receipt,
  Unlock,
  TrendingUp,
  CreditCard,
  Banknote,
  ArrowRightLeft,
  QrCode,
  AlertCircle,
  CheckCircle,
  History,
  Clock,
  MinusCircle,
  Plus,
  Trash2,
  Truck,
  Car,
  ShoppingBag,
  Wrench,
  Wallet,
  Store,
  MoreHorizontal,
  RotateCcw,
  Eye,
  User,
  Users,
  FileText,
  Check,
  AlertTriangle,
} from 'lucide-react';
import clsx from 'clsx';

// Mapeo de iconos para categorías
const CATEGORY_ICONS: Record<CashWithdrawalCategory, React.ElementType> = {
  delivery: Truck,
  transport: Car,
  supplies: ShoppingBag,
  services: Wrench,
  withdrawal: Wallet,
  supplier_payment: Store,
  salary: Users,
  other: MoreHorizontal,
};

export default function CashRegister() {
  const { user, tenant } = useAuthStore();
  const queryClient = useQueryClient();
  const [cierreZLoading, setCierreZLoading] = useState(false);
  const [showCierreZModal, setShowCierreZModal] = useState(false);
  // Carga manual de un Z que se hizo desde la impresora (el sistema no lo vio)
  const [zManualOpen, setZManualOpen] = useState(false);
  const [zManualDate, setZManualDate] = useState('');
  const [zManualNumero, setZManualNumero] = useState('');
  const [zManualSaving, setZManualSaving] = useState(false);
  // Bitácora de la impresora fiscal (diagnóstico)
  const [showBitacoraModal, setShowBitacoraModal] = useState(false);
  const [bitacoraSoloErrores, setBitacoraSoloErrores] = useState(false);
  const [bitacoraAbierta, setBitacoraAbierta] = useState<number | null>(null);

  const { data: bitacora = [], isLoading: bitacoraLoading } = useQuery({
    queryKey: ['fiscal-operaciones', bitacoraSoloErrores],
    queryFn: () => fiscalZService.getOperaciones(bitacoraSoloErrores, 50),
    enabled: showBitacoraModal,
  });

  // Estado del Cierre Z (¿el de hoy ya se hizo?, ¿quedó alguno pendiente?, histórico).
  // Se consulta aunque el modal esté cerrado para poder marcar el botón con un punto.
  const { data: zStatus, isLoading: zLoading } = useQuery({
    queryKey: ['fiscal-z-status'],
    queryFn: () => fiscalZService.getStatus(),
    enabled: !!tenant?.fiscal_enabled,
    staleTime: 60_000,
  });

  // Jornada a la que se imputa el Z que estamos por emitir.
  //
  // Proponer la jornada vieja sirve solo cuando es de hace uno o dos dias: ahi si es
  // plausible que la impresora la tenga abierta y que este Z la cierre. Si es de hace
  // semanas NO puede estar abierta, porque con una jornada trabada la Hasar no habria
  // dejado facturar en todo ese tiempo: es un registro que falta, no una jornada
  // abierta. Proponerla ahi hace que el Z de hoy se archive semanas atras — paso tres
  // veces en produccion entre el 10 y el 11/09/2026, con Z de septiembre guardados en
  // agosto. Cuando es vieja proponemos HOY y la otra queda disponible, sin marcar.
  const DIAS_PLAUSIBLES_JORNADA_VIEJA = 2;

  const [zBusinessDate, setZBusinessDate] = useState<string | null>(null);

  const diasEntre = (desde?: string | null, hasta?: string | null): number | null => {
    if (!desde || !hasta) return null;
    const a = new Date(`${desde}T00:00:00`).getTime();
    const b = new Date(`${hasta}T00:00:00`).getTime();
    if (isNaN(a) || isNaN(b)) return null;
    return Math.round((b - a) / 86400000);
  };

  const diasJornadaVieja = diasEntre(zStatus?.unclosed_previous_date, zStatus?.today);
  const jornadaViejaPlausible =
    diasJornadaVieja !== null && diasJornadaVieja <= DIAS_PLAUSIBLES_JORNADA_VIEJA;

  const zTargetDate =
    zBusinessDate ??
    (jornadaViejaPlausible ? zStatus?.unclosed_previous_date : zStatus?.today) ??
    zStatus?.today ??
    null;

  // Carga a mano un Cierre Z que ya se hizo (típico: se apretó en la impresora, o
  // se venía cerrando desde antes de que el sistema llevara este registro).
  const handleRegistrarZManual = async () => {
    if (!zManualDate) {
      toast.error('Elegí el día del cierre');
      return;
    }
    setZManualSaving(true);
    try {
      await fiscalZService.register(zManualNumero || null, zManualDate);
      await queryClient.invalidateQueries({ queryKey: ['fiscal-z-status'] });
      toast.success('Cierre Z registrado');
      setZManualOpen(false);
      setZManualNumero('');
      setZManualDate('');
    } catch (e: any) {
      toast.error(`No se pudo registrar: ${e?.response?.data?.detail || e?.message || 'error'}`);
    } finally {
      setZManualSaving(false);
    }
  };

  // Manda el cierre a la impresora y, si sale bien, lo registra en el sistema.
  //
  // OJO con la fecha: el Z que emite la Hasar cierra la jornada mas vieja que tenga
  // abierta, que no siempre es la de hoy. Si dejamos que el backend asuma "hoy",
  // la jornada vieja queda huerfana y el aviso de "sin cerrar" no se va nunca.
  // Por eso mandamos explicitamente el dia que el cajero confirmo contra el ticket.
  // Reporte X: imprime el acumulado del dia SIN cerrar la jornada. Es la unica forma
  // de probar si la impresora esta respondiendo sin arriesgar nada, porque el Cierre Z
  // no tiene vuelta atras.
  const [reporteXLoading, setReporteXLoading] = useState(false);

  const handleReporteX = async () => {
    setReporteXLoading(true);
    try {
      await reporteXFiscal();
      toast.success('Reporte X emitido. Fijate si salio por el papel de la impresora.');
    } catch (e: any) {
      toast.error(`No se pudo emitir el Reporte X: ${e?.message || 'agente fiscal no disponible'}`);
    } finally {
      setReporteXLoading(false);
    }
  };

  const handleCierreZ = async () => {
    setCierreZLoading(true);
    try {
      const numero = await cierreZFiscal();
      try {
        await fiscalZService.register(numero, zTargetDate || undefined);
        queryClient.invalidateQueries({ queryKey: ['fiscal-z-status'] });
      } catch {
        // El cierre YA se hizo en la impresora: no lo tratamos como error, solo
        // no queda en el histórico del sistema.
        toast('El cierre se hizo, pero no se pudo registrar en el historial', { icon: '⚠️' });
      }
      toast.success(`Cierre Z realizado${numero ? ` — N° ${numero}` : ''}`);
      setShowCierreZModal(false);
    } catch (e: any) {
      toast.error(`No se pudo hacer el Cierre Z: ${e?.message || 'agente fiscal no disponible'}`);
    } finally {
      setCierreZLoading(false);
    }
  };
  const isAdmin = user?.role === 'admin';
  // Info financiera (ganancia, costo, gastos): solo admin/superadmin o quien pueda ver costos.
  // El empleado ve la caja (efectivo, ventas, esperado) pero NO la ganancia/costo/gastos.
  const canViewFinance = user?.role === 'admin' || user?.role === 'superadmin'
    || (user?.effective_permissions?.includes('products_view_costs') ?? false);

  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenRegisterId, setReopenRegisterId] = useState<number | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [viewRegisterId, setViewRegisterId] = useState<number | null>(null);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [openingNotes, setOpeningNotes] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [closeType, setCloseType] = useState<CloseType>('final');

  // Estados para salidas de caja
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalCategory, setWithdrawalCategory] = useState<CashWithdrawalCategory>('other');
  const [withdrawalPaymentMethod, setWithdrawalPaymentMethod] = useState<PaymentMethod>('cash');
  const [withdrawalDescription, setWithdrawalDescription] = useState('');
  const [withdrawalRecipient, setWithdrawalRecipient] = useState('');
  const [deleteWithdrawalId, setDeleteWithdrawalId] = useState<number | null>(null);

  // Valores numericos derivados (parseo formato AR "$1.234,56")
  const closingCashNum = parseARNumber(closingCash) ?? 0;
  const withdrawalAmountNum = parseARNumber(withdrawalAmount) ?? 0;

  const { data: status, isLoading } = useCashRegisterStatus();
  const { data: history } = useCashRegisters({ limit: 100 });
  const { data: profitHistory } = useProfitHistory(30);
  const { data: withdrawals = [] } = useCashWithdrawals(undefined, status?.is_open);

  // Para ver detalle de una caja del historial
  const { data: viewRegisterData } = useCashRegister(viewRegisterId);
  const { data: viewRegisterWithdrawals = [] } = useCashWithdrawals(viewRegisterId || undefined, !!viewRegisterId);
  const openRegister = useOpenCashRegister();
  const closeRegister = useCloseCashRegister();
  const reopenRegister = useReopenCashRegister();
  const createWithdrawal = useCreateCashWithdrawal();
  const deleteWithdrawal = useDeleteCashWithdrawal();

  const handleOpenRegister = async () => {
    const parsed = parseARNumber(openingCash);
    if (parsed == null) return;
    try {
      await openRegister.mutateAsync({
        opening_cash: parsed,
        opening_notes: openingNotes || undefined,
        // Si el día ya tiene cierre final, este turno lo está abriendo el admin
        // a propósito para corregir un cierre equivocado.
        force_after_final: diaYaCerrado || undefined,
      });
      setShowOpenModal(false);
      setOpeningCash('');
      setOpeningNotes('');
    } catch (error: any) {
      console.error('Error opening register:', error);
      const detalle = error?.response?.data?.detail;
      toast.error(typeof detalle === 'string' ? detalle : 'No se pudo abrir la caja', { duration: 8000 });
    }
  };

  // Paso 1: Abrir modal de confirmación
  const handleRequestClose = () => {
    if (!closingCash) return;
    setShowCloseModal(false);
    setShowConfirmCloseModal(true);
  };

  // Paso 2: Confirmar y cerrar caja
  const handleCloseRegister = async () => {
    if (!closingCash || !status?.current_register) return;
    try {
      await closeRegister.mutateAsync({
        id: status.current_register.id,
        data: {
          closing_cash: closingCashNum,
          closing_notes: closingNotes || undefined,
          close_type: closeType,
        },
      });
      setShowConfirmCloseModal(false);
      setClosingCash('');
      setClosingNotes('');
      setCloseType('final');
    } catch (error) {
      console.error('Error closing register:', error);
    }
  };

  // Cancelar confirmación y volver al modal anterior
  const handleCancelConfirm = () => {
    setShowConfirmCloseModal(false);
    setShowCloseModal(true);
  };

  // Handler para crear salida de caja
  const handleCreateWithdrawal = async () => {
    if (!withdrawalAmount) return;
    try {
      await createWithdrawal.mutateAsync({
        amount: withdrawalAmountNum,
        category: withdrawalCategory,
        payment_method: withdrawalPaymentMethod,
        description: withdrawalDescription || undefined,
        recipient: withdrawalRecipient || undefined,
      });
      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setWithdrawalCategory('other');
      setWithdrawalPaymentMethod('cash');
      setWithdrawalDescription('');
      setWithdrawalRecipient('');
    } catch (error) {
      console.error('Error creating withdrawal:', error);
    }
  };

  // Handler para eliminar salida de caja
  const handleDeleteWithdrawal = async () => {
    if (!deleteWithdrawalId) return;
    try {
      await deleteWithdrawal.mutateAsync(deleteWithdrawalId);
      setDeleteWithdrawalId(null);
    } catch (error) {
      console.error('Error deleting withdrawal:', error);
    }
  };

  // Handler para reabrir caja (solo admin)
  const handleRequestReopen = (registerId: number) => {
    setReopenRegisterId(registerId);
    setShowReopenModal(true);
  };

  const handleReopenRegister = async () => {
    if (!reopenRegisterId) return;
    try {
      await reopenRegister.mutateAsync(reopenRegisterId);
      toast.success('Caja reabierta');
      setShowReopenModal(false);
      setReopenRegisterId(null);
      setShowHistoryModal(false);
    } catch (error: any) {
      // Antes esto solo iba a la consola: el backend explicaba por que no podia
      // ("ya existe una caja abierta", "solo el administrador...") y en pantalla no
      // pasaba nada, asi que parecia que el boton estaba roto.
      console.error('Error reopening register:', error);
      const detalle = error?.response?.data?.detail;
      const sinRespuesta =
        error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK' || !error?.response;
      toast.error(
        sinRespuesta
          ? 'Se cortó la conexión con el servidor y no se pudo reabrir la caja. Probá de nuevo.'
          : (typeof detalle === 'string' ? detalle : 'No se pudo reabrir la caja'),
        { duration: 8000 }
      );
    }
  };

  // El día ya tiene cierre final. Normalmente no se abren más turnos, pero el error
  // mas comun del mostrador es elegir "final" en lugar de "parcial", y sin una salida
  // el dia quedaba bloqueado hasta el otro dia.
  const diaYaCerrado = !!status?.final_close_today && !status?.is_open;
  const puedeAbrirIgual = diaYaCerrado && (isAdmin || user?.role === 'superadmin');

  const currentRegister = status?.current_register;
  // Total de TODAS las salidas (para mostrar en UI)
  const totalWithdrawals = currentRegister?.total_withdrawals || 0;
  const totalExpenses = currentRegister?.total_expenses || 0;
  // Efectivo esperado = inicial + ventas efectivo - salidas de efectivo SOLAMENTE
  const totalCashWithdrawals = currentRegister?.total_cash_withdrawals || 0;
  const expectedCash = currentRegister
    ? Number(currentRegister.opening_cash) + Number(currentRegister.total_cash_sales) - Number(totalCashWithdrawals)
    : 0;

  // Separar gastos, pagos a proveedores y retiros
  const expenseWithdrawals = withdrawals.filter((w) => !['withdrawal', 'supplier_payment', 'salary'].includes(w.category));
  const salaryWithdrawals = withdrawals.filter((w) => w.category === 'salary');
  const supplierPaymentWithdrawals = withdrawals.filter((w) => w.category === 'supplier_payment');
  const safeWithdrawals = withdrawals.filter((w) => w.category === 'withdrawal');
  const totalSalaries = salaryWithdrawals.reduce((sum, w) => sum + Number(w.amount), 0);
  const totalSupplierPayments = supplierPaymentWithdrawals.reduce((sum, w) => sum + Number(w.amount), 0);
  const totalSafeWithdrawals = safeWithdrawals.reduce((sum, w) => sum + Number(w.amount), 0);

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Caja Diaria</h1>
          <p className="text-gray-600">Control de apertura y cierre de caja</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowHistoryModal(true)}>
            <History className="h-4 w-4 mr-2" />
            Historial
          </Button>
          {tenant?.fiscal_enabled && isAdmin && (
            <Button
              variant="secondary"
              title="Ver qué le pasó a la impresora fiscal en cada venta (para diagnosticar)"
              onClick={() => setShowBitacoraModal(true)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Diagnóstico fiscal
            </Button>
          )}
          {tenant?.fiscal_enabled && (
            <Button
              variant="secondary"
              title="Cierre Z: cierra la jornada fiscal del día e imprime el reporte Z (hacerlo una vez, al final del día)"
              onClick={() => setShowCierreZModal(true)}
            >
              <Receipt className="h-4 w-4 mr-2" />
              Cierre Z fiscal
              {zStatus && !zStatus.done_today && (zStatus.pending_sales_count > 0 || zStatus.unclosed_previous_date) && (
                <span
                  className="ml-2 h-2 w-2 rounded-full bg-amber-500"
                  title="Hay una jornada fiscal sin cerrar"
                />
              )}
            </Button>
          )}
          {status?.is_open && (
            <Button variant="secondary" onClick={() => setShowWithdrawalModal(true)}>
              <MinusCircle className="h-4 w-4 mr-2" />
              Registrar Salida
            </Button>
          )}
          {status?.is_open ? (
            <Button variant="primary" onClick={() => setShowCloseModal(true)}>
              <Lock className="h-4 w-4 mr-2" />
              Cerrar Caja
            </Button>
          ) : diaYaCerrado ? (
            puedeAbrirIgual ? (
              <Button
                onClick={() => setShowOpenModal(true)}
                className="bg-amber-600 hover:bg-amber-700"
                title="El día ya tiene cierre final. Abrir otro turno es una corrección."
              >
                <Unlock className="h-4 w-4 mr-2" />
                Abrir turno igual
              </Button>
            ) : (
              <span className="text-sm text-gray-500 self-center">
                El día ya se cerró. Para abrir otro turno, pedíselo al administrador.
              </span>
            )
          ) : (
            <Button onClick={() => setShowOpenModal(true)}>
              <Unlock className="h-4 w-4 mr-2" />
              Abrir Caja
            </Button>
          )}
        </div>
      </div>

      {/* Estado actual */}
      <div className={clsx(
        'rounded-2xl p-6 border-2',
        status?.is_open
          ? 'bg-green-50 border-green-200'
          : 'bg-gray-50 border-gray-200'
      )}>
        <div className="flex items-center gap-3 mb-4">
          {status?.is_open ? (
            <>
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                <Unlock className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-green-800">
                  Caja Abierta
                  {currentRegister && currentRegister.shift_number > 1 && (
                    <span className="ml-2 text-sm font-normal bg-green-200 text-green-700 px-2 py-0.5 rounded-full">
                      Turno {currentRegister.shift_number}
                    </span>
                  )}
                </h2>
                <p className="text-sm text-green-600">
                  Abierta el {formatDateTime(currentRegister?.opened_at)}
                  {currentRegister?.opened_by && ` por ${currentRegister.opened_by.full_name}`}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-12 h-12 bg-gray-400 rounded-xl flex items-center justify-center">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-700">Caja Cerrada</h2>
                <p className="text-sm text-gray-500">
                  {status?.last_closed_register
                    ? `Última caja cerrada el ${formatDateTime(status.last_closed_register.closed_at)}`
                    : 'No hay registros de caja'}
                  {status?.last_closed_register?.auto_closed && (
                    <span className="ml-2 text-amber-600">(Cierre automático)</span>
                  )}
                </p>
              </div>
              {/* Botón reabrir para admin */}
              {(isAdmin || user?.role === 'superadmin') && status?.last_closed_register && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRequestReopen(status.last_closed_register!.id)}
                  className="ml-4"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reabrir Caja
                </Button>
              )}
            </>
          )}
        </div>

        {/* Resumen de caja abierta */}
        {currentRegister && (
          <div className={`grid grid-cols-2 ${canViewFinance ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 mt-6`}>
            <div className="bg-white rounded-xl p-4 border border-green-100">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <Banknote className="h-5 w-5" />
                <span className="text-sm font-medium">Efectivo Inicial</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(currentRegister.opening_cash)}
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <DollarSign className="h-5 w-5" />
                <span className="text-sm font-medium">Ventas Totales</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(currentRegister.total_sales)}
              </p>
            </div>

            {canViewFinance && (
              <div className="bg-white rounded-xl p-4 border border-emerald-100">
                <div className="flex items-center gap-2 text-emerald-600 mb-2">
                  <TrendingUp className="h-5 w-5" />
                  <span className="text-sm font-medium">Ganancia</span>
                </div>
                <p className={`text-2xl font-bold ${currentRegister.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatPrice(currentRegister.total_profit)}
                </p>
                {totalExpenses > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Neta (gastos: -{formatPrice(totalExpenses)})
                  </p>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl p-4 border border-amber-100">
              <div className="flex items-center gap-2 text-amber-600 mb-2">
                <Banknote className="h-5 w-5" />
                <span className="text-sm font-medium">Efectivo Esperado</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(expectedCash)}
              </p>
            </div>
          </div>
        )}

        {/* Desglose por método de pago */}
        {currentRegister && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Banknote className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  {/* Se llamaba solo "Efectivo" y se leia como la plata del cajon,
                      cuando mide las VENTAS cobradas en efectivo: el numero no baja
                      aunque se saque plata. El neto va abajo, explicito. */}
                  <p className="text-xs text-gray-500">Ventas en efectivo</p>
                  <p className="font-bold text-gray-900">{formatPrice(currentRegister.total_cash_sales)}</p>
                </div>
              </div>
              {(currentRegister.total_cash_withdrawals || 0) > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-red-600">
                  Salidas: -{formatPrice(currentRegister.total_cash_withdrawals)}
                </div>
              )}
              <div className="mt-1 text-xs text-gray-600 flex justify-between">
                <span>En caja:</span>
                <span className="font-semibold text-gray-900">{formatPrice(expectedCash)}</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Tarjeta</p>
                  <p className="font-bold text-gray-900">{formatPrice(currentRegister.total_card_sales)}</p>
                </div>
              </div>
              {(currentRegister.total_card_withdrawals || 0) > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-red-600">
                  Salidas: -{formatPrice(currentRegister.total_card_withdrawals)}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ArrowRightLeft className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Transferencia</p>
                  <p className="font-bold text-gray-900">{formatPrice(currentRegister.total_transfer_sales)}</p>
                </div>
              </div>
              {(currentRegister.total_transfer_withdrawals || 0) > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-red-600">
                  Salidas: -{formatPrice(currentRegister.total_transfer_withdrawals)}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <QrCode className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">QR</p>
                  <p className="font-bold text-gray-900">{formatPrice(currentRegister.total_qr_sales)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gastos (afectan ganancia) */}
        {currentRegister && expenseWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-red-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-red-600">
                <MinusCircle className="h-5 w-5" />
                <span className="font-medium">Gastos</span>
              </div>
              <span className="text-xl font-bold text-red-600">-{formatPrice(totalExpenses)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">Restan de la ganancia</p>
            <div className="space-y-2">
              {expenseWithdrawals.map((w) => {
                const Icon = CATEGORY_ICONS[w.category] || MoreHorizontal;
                return (
                  <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                        <Icon className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{w.description || WITHDRAWAL_CATEGORY_LABELS[w.category]}</p>
                        <p className="text-xs text-gray-500">
                          {WITHDRAWAL_CATEGORY_LABELS[w.category]}
                          {w.payment_method && ` • ${PAYMENT_METHOD_LABELS[w.payment_method]}`}
                          {w.recipient && ` • ${w.recipient}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-red-600">-{formatPrice(w.amount)}</span>
                      <button
                        onClick={() => setDeleteWithdrawalId(w.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pagos a Proveedores (NO afectan ganancia, solo saldo de caja) */}
        {currentRegister && supplierPaymentWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-amber-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-amber-700">
                <Store className="h-5 w-5" />
                <span className="font-medium">Pagos a Proveedores</span>
              </div>
              <span className="text-xl font-bold text-amber-700">-{formatPrice(totalSupplierPayments)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">Reducen el saldo de caja, no afectan la ganancia</p>
            <div className="space-y-2">
              {supplierPaymentWithdrawals.map((w) => (
                <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                      <Store className="h-4 w-4 text-amber-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{w.description || WITHDRAWAL_CATEGORY_LABELS[w.category]}</p>
                      <p className="text-xs text-gray-500">
                        {PAYMENT_METHOD_LABELS[w.payment_method]}
                        {w.recipient && ` • ${w.recipient}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-amber-700">-{formatPrice(w.amount)}</span>
                    <button
                      onClick={() => setDeleteWithdrawalId(w.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sueldos (afectan ganancia) */}
        {currentRegister && salaryWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-indigo-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-indigo-700">
                <Users className="h-5 w-5" />
                <span className="font-medium">Sueldos</span>
              </div>
              <span className="text-xl font-bold text-indigo-700">-{formatPrice(totalSalaries)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">Afectan la ganancia del negocio</p>
            <div className="space-y-2">
              {salaryWithdrawals.map((w) => (
                <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center">
                      <Users className="h-4 w-4 text-indigo-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{w.recipient || 'Sueldo'}</p>
                      <p className="text-xs text-gray-500">
                        {PAYMENT_METHOD_LABELS[w.payment_method]}
                        {w.description && ` • ${w.description}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-indigo-700">-{formatPrice(w.amount)}</span>
                    <button
                      onClick={() => setDeleteWithdrawalId(w.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Retiros (NO afectan ganancia) */}
        {currentRegister && safeWithdrawals.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-xl border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-blue-600">
                <Banknote className="h-5 w-5" />
                <span className="font-medium">Retiros</span>
              </div>
              <span className="text-xl font-bold text-blue-600">-{formatPrice(totalSafeWithdrawals)}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">No afectan la ganancia</p>
            <div className="space-y-2">
              {safeWithdrawals.map((w) => {
                const Icon = CATEGORY_ICONS[w.category] || MoreHorizontal;
                return (
                  <div key={w.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Icon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{w.description || WITHDRAWAL_CATEGORY_LABELS[w.category]}</p>
                        <p className="text-xs text-gray-500">
                          {WITHDRAWAL_CATEGORY_LABELS[w.category]}
                          {w.payment_method && ` • ${PAYMENT_METHOD_LABELS[w.payment_method]}`}
                          {w.recipient && ` • ${w.recipient}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-blue-600">-{formatPrice(w.amount)}</span>
                      <button
                        onClick={() => setDeleteWithdrawalId(w.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Historial de ganancias (solo finanzas/admin) */}
      {canViewFinance && profitHistory && profitHistory.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Historial de Ganancias
            </h3>
          </div>
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ventas</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Costo</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gastos</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ganancia</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Dif. Caja</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {profitHistory.slice(0, 10).map((day, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(day.date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatPrice(day.total_sales)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">
                        {formatPrice(day.total_cost)}
                      </td>
                      <td className="px-4 py-3 text-sm text-red-500 text-right">
                        {(day.total_expenses || 0) > 0 ? `-${formatPrice(day.total_expenses)}` : '-'}
                      </td>
                      <td className={clsx(
                        'px-4 py-3 text-sm font-medium text-right',
                        day.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'
                      )}>
                        {formatPrice(day.total_profit)}
                      </td>
                      <td className={clsx(
                        'px-4 py-3 text-sm text-right',
                        day.cash_difference === 0
                          ? 'text-gray-500'
                          : day.cash_difference > 0
                          ? 'text-blue-600'
                          : 'text-red-600'
                      )}>
                        {day.cash_difference > 0 && '+'}
                        {formatPrice(day.cash_difference)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal Diagnóstico fiscal: qué le pasó a la impresora en cada operación.
          Guardamos también las exitosas: son el punto de comparación cuando hay
          que entender por qué falló una. */}
      <Modal
        isOpen={showBitacoraModal}
        onClose={() => setShowBitacoraModal(false)}
        title="Diagnóstico fiscal"
        size="xl"
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Últimas operaciones contra la impresora fiscal. Tocá una para ver la
              secuencia exacta de comandos.
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
              <input
                type="checkbox"
                checked={bitacoraSoloErrores}
                onChange={(e) => setBitacoraSoloErrores(e.target.checked)}
              />
              Solo las que fallaron
            </label>
          </div>

          {bitacoraLoading ? (
            <p className="text-center text-gray-500 py-4">Cargando...</p>
          ) : bitacora.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">
              No hay operaciones registradas todavía. Se van guardando a medida que
              se factura.
            </p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {bitacora.map((op) => (
                <div
                  key={op.id}
                  className={clsx(
                    'rounded-xl border p-3',
                    op.ok ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50',
                  )}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setBitacoraAbierta(bitacoraAbierta === op.id ? null : op.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800">
                          {op.ok ? '✅' : '❌'} {op.operacion}
                          {op.numero_comprobante ? ` — N° ${op.numero_comprobante}` : ''}
                          {op.sale_id ? ` · venta #${op.sale_id}` : ''}
                        </p>
                        {!op.ok && (
                          <p className="text-sm text-red-700 mt-0.5">
                            {op.identificador ? <strong>{op.identificador}</strong> : null}
                            {op.comando ? ` en ${op.comando}` : ''}
                            {op.mensaje ? `: ${op.mensaje}` : ''}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs text-gray-500 whitespace-nowrap">
                        <p>{formatDateTime(op.created_at)}</p>
                        {op.duracion_ms != null && <p>{(op.duracion_ms / 1000).toFixed(1)}s</p>}
                        {op.created_by_name && <p>{op.created_by_name}</p>}
                      </div>
                    </div>
                  </button>

                  {bitacoraAbierta === op.id && (
                    op.trace ? (
                      <pre className="mt-2 p-2 bg-gray-900 text-gray-100 text-[11px] rounded-lg overflow-x-auto whitespace-pre">
                        {op.trace}
                      </pre>
                    ) : (
                      <p className="mt-2 text-xs text-gray-500">
                        Sin traza. La manda el agente de la caja: si falta, ese agente
                        todavía no está actualizado.
                      </p>
                    )
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Cierre Z fiscal: estado del día + histórico, para que el cajero
          sepa si ya lo hizo antes de volver a apretar */}
      <Modal
        isOpen={showCierreZModal}
        onClose={() => setShowCierreZModal(false)}
        title="Cierre Z fiscal"
        size="lg"
      >
        <div className="space-y-4">
          {zLoading ? (
            <p className="text-center text-gray-500 py-4">Cargando estado...</p>
          ) : (
            <>
              {/* Estado de hoy */}
              {zStatus?.done_today ? (
                <div className="p-4 rounded-xl bg-green-50 border border-green-200 flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-green-800">El Cierre Z de hoy ya está hecho</p>
                    <p className="text-sm text-green-700 mt-0.5">
                      {zStatus.today_closure?.numero ? `N° ${zStatus.today_closure.numero} · ` : ''}
                      {formatDateTime(zStatus.today_closure?.closed_at)}
                      {zStatus.today_closure?.closed_by_name ? ` · por ${zStatus.today_closure.closed_by_name}` : ''}
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      No hace falta hacerlo de nuevo. Si lo repetís, la impresora va a emitir
                      otro Z (queda en cero, pero suma un número).
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">Todavía no se hizo el Cierre Z de hoy</p>
                    <p className="text-sm text-amber-700 mt-0.5">
                      {zStatus && zStatus.pending_sales_count > 0
                        ? `Hoy se facturaron ${zStatus.pending_sales_count} venta(s) por la Hasar (${formatPrice(zStatus.pending_sales_total)}).`
                        : 'Hoy todavía no se facturó nada por la Hasar.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Jornada vieja sin cerrar: es la que traba la impresora al otro día */}
              {zStatus?.unclosed_previous_date && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">
                      Quedó sin cerrar la jornada del {formatDate(zStatus.unclosed_previous_date)}
                    </p>
                    <p className="text-sm text-red-700 mt-0.5">
                      Mientras haya una jornada vieja abierta, la impresora fiscal no deja emitir
                      comprobantes nuevos. Hacé el Cierre Z ahora para destrabarla.
                    </p>
                  </div>
                </div>
              )}

              {/* A que jornada se imputa el Z. Solo tiene sentido preguntarlo cuando hay
                  una jornada vieja abierta: ahi es donde el sistema se equivocaba
                  registrando todo como "hoy". */}
              {zStatus?.unclosed_previous_date && (
                <div className="p-4 rounded-xl bg-white border border-gray-200">
                  <p className="text-sm font-semibold text-gray-800">
                    ¿A qué jornada corresponde este Cierre Z?
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    La impresora cierra la jornada más vieja que tenga abierta, que puede no
                    ser la de hoy. Elegí el día que figura en el ticket Z para que el historial
                    quede derecho.
                  </p>
                  {!jornadaViejaPlausible && diasJornadaVieja !== null && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-2">
                      Esa jornada es de hace {diasJornadaVieja} días. Si la impresora la
                      tuviera abierta no habrías podido facturar en todo ese tiempo, así que
                      casi seguro es un <strong>registro que falta</strong>, no una jornada
                      trabada. Salvo que el ticket Z diga esa fecha, dejá marcado <strong>Hoy</strong>{' '}
                      y cargá ese día aparte con "Registrar un Cierre Z que ya hice".
                    </p>
                  )}
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {([
                      [zStatus.unclosed_previous_date, `Jornada del ${formatDate(zStatus.unclosed_previous_date)}`, 'La que quedó abierta'],
                      [zStatus.today, `Hoy, ${formatDate(zStatus.today)}`, 'Si la vieja ya se cerró aparte'],
                    ] as [string, string, string][]).map(([value, label, hint]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setZBusinessDate(value)}
                        className={`text-left px-3 py-2 rounded-lg border transition-all ${
                          zTargetDate === value
                            ? 'border-taupe-500 bg-taupe-50 ring-1 ring-taupe-500'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="block text-sm font-medium text-gray-900">{label}</span>
                        <span className="block text-xs text-gray-500">{hint}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Histórico */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Últimos cierres</p>
                {zStatus?.history?.length ? (
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Día</th>
                          <th className="text-left px-3 py-2 font-medium">N° Z</th>
                          <th className="text-left px-3 py-2 font-medium">Hecho</th>
                          <th className="text-left px-3 py-2 font-medium">Por</th>
                          <th className="text-right px-3 py-2 font-medium">Ventas fiscales</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {zStatus.history.map((z) => (
                          <tr key={z.id}>
                            <td className="px-3 py-2">{formatDate(z.business_date)}</td>
                            <td className="px-3 py-2 tabular-nums">{z.numero || '-'}</td>
                            <td className="px-3 py-2 text-gray-500">{formatDateTime(z.closed_at)}</td>
                            <td className="px-3 py-2 text-gray-500">{z.closed_by_name || '-'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {z.fiscal_sales_count} · {formatPrice(z.fiscal_sales_total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Todavía no hay cierres registrados por el sistema.
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  {zStatus?.tracking_since
                    ? `El sistema lleva registro desde el ${formatDate(zStatus.tracking_since)}. Los cierres hechos directo en la impresora no figuran acá salvo que los cargues.`
                    : 'El sistema todavía no tiene ningún cierre registrado, así que no puede avisarte si quedó alguna jornada sin cerrar. Cargá el último Z que hiciste y desde ahí empieza a controlarlo.'}
                </p>
              </div>

              {/* Cargar un Z hecho desde la impresora */}
              <div className="rounded-xl border border-gray-200 p-3">
                {!zManualOpen ? (
                  <button
                    type="button"
                    className="text-sm text-teal-700 hover:text-teal-800 font-medium"
                    onClick={() => {
                      const ayer = new Date();
                      ayer.setDate(ayer.getDate() - 1);
                      setZManualDate(ayer.toISOString().slice(0, 10));
                      setZManualOpen(true);
                    }}
                  >
                    + Registrar un Cierre Z que ya hice
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Cargá un cierre que ya se hizo en la impresora. El N° lo ves en el
                      papel del reporte, arriba: "INFORME DIARIO DE CIERRE N°".
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        type="date"
                        label="Día de la jornada"
                        value={zManualDate}
                        onChange={(e) => setZManualDate(e.target.value)}
                      />
                      <Input
                        label="N° de Z (opcional)"
                        placeholder="14"
                        value={zManualNumero}
                        onChange={(e) => setZManualNumero(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => setZManualOpen(false)}>
                        Cancelar
                      </Button>
                      <Button
                        variant="primary"
                        loading={zManualSaving}
                        onClick={handleRegistrarZManual}
                      >
                        Registrar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 flex-wrap">
                <Button variant="secondary" onClick={() => setShowCierreZModal(false)}>
                  Cancelar
                </Button>
                <Button
                  variant="secondary"
                  loading={reporteXLoading}
                  onClick={handleReporteX}
                  title="Imprime el acumulado del día sin cerrar la jornada. Sirve para probar si la impresora responde."
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Probar con Reporte X
                </Button>
                <Button variant="primary" loading={cierreZLoading} onClick={handleCierreZ}>
                  <Receipt className="h-4 w-4 mr-2" />
                  {zStatus?.unclosed_previous_date && zTargetDate === zStatus.unclosed_previous_date
                    ? `Cerrar la jornada del ${formatDate(zStatus.unclosed_previous_date)}`
                    : zStatus?.done_today ? 'Hacer otro Cierre Z igual' : 'Hacer el Cierre Z'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Modal Abrir Caja */}
      <Modal
        isOpen={showOpenModal}
        onClose={() => setShowOpenModal(false)}
        title={status?.can_open_new_shift ? `Abrir Turno ${status.next_shift_number}` : "Abrir Caja"}
      >
        <div className="space-y-4">
          {/* Info del turno anterior si hay cierre parcial */}
          {status?.last_partial_close_today && (
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-200 text-amber-800 text-sm font-bold">
                  {status.last_partial_close_today.shift_number}
                </span>
                <div>
                  <span className="font-medium text-amber-800">Turno {status.last_partial_close_today.shift_number} finalizado</span>
                  <p className="text-xs text-amber-600">Cierre parcial del día</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-white/50 rounded-lg p-2">
                  <p className="text-xs text-amber-600">Efectivo al cierre</p>
                  <p className="font-bold text-amber-800">{formatPrice(status.last_partial_close_today.closing_cash || 0)}</p>
                </div>
                <div className="bg-white/50 rounded-lg p-2">
                  <p className="text-xs text-amber-600">Ventas del turno</p>
                  <p className="font-bold text-amber-800">{formatPrice(status.last_partial_close_today.total_sales || 0)}</p>
                </div>
              </div>
              <p className="text-xs text-amber-700 mt-2">
                Puede usar el efectivo de cierre como base para el nuevo turno o ingresar un monto diferente.
              </p>
            </div>
          )}

          <div className="p-4 bg-blue-50 rounded-xl">
            <div className="flex items-center gap-3">
              {diaYaCerrado && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 text-sm">
                  <p className="font-medium text-amber-900">
                    Hoy ya se hizo el cierre final del día.
                  </p>
                  <p className="text-amber-800 mt-1">
                    Abrir otro turno es una corrección: se usa cuando el cierre final se
                    hizo por error. El turno anterior queda como está, con su arqueo.
                  </p>
                </div>
              )}
              {status?.can_open_new_shift && (
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-200 text-blue-800 text-lg font-bold">
                  {status.next_shift_number}
                </span>
              )}
              <p className="text-sm text-blue-800">
                {status?.can_open_new_shift
                  ? `Va a iniciar el turno ${status.next_shift_number} del día. Ingrese el efectivo inicial.`
                  : 'Ingrese el monto de efectivo con el que inicia la caja del día.'}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Efectivo Inicial
            </label>
            <CurrencyInput
              value={openingCash}
              onChange={setOpeningCash}
              className="py-3 text-base"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Notas (opcional)
            </label>
            <textarea
              value={openingNotes}
              onChange={(e) => setOpeningNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              placeholder="Observaciones de apertura..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowOpenModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleOpenRegister}
              loading={openRegister.isPending}
              disabled={!openingCash}
            >
              <Unlock className="h-4 w-4 mr-2" />
              Abrir Caja
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Cerrar Caja */}
      <Modal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        title={currentRegister && currentRegister.shift_number > 1
          ? `Cerrar Turno ${currentRegister.shift_number}`
          : "Cerrar Caja"}
        size="lg"
      >
        <div className="space-y-4">
          {/* Info del turno actual */}
          {currentRegister && currentRegister.shift_number > 1 && (
            <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-2 text-purple-800">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-200 text-purple-800 text-xs font-bold">
                  {currentRegister.shift_number}
                </span>
                <span className="text-sm font-medium">
                  Estás cerrando el turno {currentRegister.shift_number} del día
                </span>
              </div>
            </div>
          )}

          {/* Resumen antes de cerrar */}
          {currentRegister && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Efectivo Inicial</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatPrice(currentRegister.opening_cash)}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl">
                <p className="text-sm text-green-600 mb-1">Ventas en Efectivo</p>
                <p className="text-xl font-bold text-green-700">
                  +{formatPrice(currentRegister.total_cash_sales)}
                </p>
              </div>
              {totalWithdrawals > 0 && (
                <div className="p-4 bg-red-50 rounded-xl col-span-2">
                  <p className="text-sm text-red-600 mb-1">Salidas de Caja</p>
                  <p className="text-xl font-bold text-red-700">
                    -{formatPrice(totalWithdrawals)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Ventas del día por medio de pago (incluye no-efectivo: tarjeta/transf/QR) */}
          {currentRegister && (
            <div className="p-4 bg-gray-50 rounded-xl text-sm space-y-1.5">
              <p className="text-gray-500 font-medium mb-1">Ventas del día por medio</p>
              <div className="flex justify-between"><span className="text-gray-600">Efectivo</span><span className="font-medium">{formatPrice(currentRegister.total_cash_sales)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Tarjeta</span><span className="font-medium">{formatPrice(currentRegister.total_card_sales)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Transferencia</span><span className="font-medium">{formatPrice(currentRegister.total_transfer_sales)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">QR</span><span className="font-medium">{formatPrice(currentRegister.total_qr_sales)}</span></div>
              <div className="flex justify-between border-t border-gray-200 pt-1.5 font-bold text-gray-900"><span>Total ventas</span><span>{formatPrice(currentRegister.total_sales)}</span></div>
            </div>
          )}

          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 text-blue-800 mb-1">
              <DollarSign className="h-5 w-5" />
              <span className="font-medium">Efectivo Esperado en Caja</span>
            </div>
            <p className="text-3xl font-bold text-blue-900">
              {formatPrice(expectedCash)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Efectivo Real en Caja
            </label>
            <CurrencyInput
              value={closingCash}
              onChange={setClosingCash}
              className="py-3 text-base"
            />
          </div>

          {closingCash && (
            <div className={clsx(
              'p-4 rounded-xl flex items-center gap-3',
              closingCashNum === expectedCash
                ? 'bg-green-50 border border-green-200'
                : closingCashNum > expectedCash
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-red-50 border border-red-200'
            )}>
              {closingCashNum === expectedCash ? (
                <>
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Caja Cuadrada</p>
                    <p className="text-sm text-green-600">El efectivo coincide con lo esperado</p>
                  </div>
                </>
              ) : closingCashNum > expectedCash ? (
                <>
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">Sobrante</p>
                    <p className="text-sm text-blue-600">
                      {formatPrice(closingCashNum - expectedCash)} de más
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="h-6 w-6 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Faltante</p>
                    <p className="text-sm text-red-600">
                      {formatPrice(expectedCash - closingCashNum)} de menos
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Notas de Cierre (opcional)
            </label>
            <textarea
              value={closingNotes}
              onChange={(e) => setClosingNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              placeholder="Observaciones de cierre..."
            />
          </div>

          {/* Tipo de cierre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Cómo desea cerrar{currentRegister && currentRegister.shift_number > 1 ? ` el turno ${currentRegister.shift_number}` : ''}?
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCloseType('partial')}
                className={clsx(
                  'p-4 rounded-xl border-2 text-left transition-all',
                  closeType === 'partial'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Clock className={clsx('h-5 w-5', closeType === 'partial' ? 'text-orange-600' : 'text-gray-400')} />
                  <span className={clsx('font-medium', closeType === 'partial' ? 'text-orange-800' : 'text-gray-700')}>
                    Cierre Parcial
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Finaliza este turno. Se podrá abrir el turno {(currentRegister?.shift_number || 1) + 1} después.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setCloseType('final')}
                className={clsx(
                  'p-4 rounded-xl border-2 text-left transition-all',
                  closeType === 'final'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Lock className={clsx('h-5 w-5', closeType === 'final' ? 'text-red-600' : 'text-gray-400')} />
                  <span className={clsx('font-medium', closeType === 'final' ? 'text-red-800' : 'text-gray-700')}>
                    Cierre Final
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Cierre del día completo. No más turnos hoy.
                </p>
              </button>
            </div>

            {/* Las dos opciones se ven igual de inocentes y elegir "final" por error
                dejaba el dia bloqueado. El aviso aparece recien al elegirla, para que
                no se vuelva ruido de fondo. */}
            {closeType === 'final' && (
              <div className="mt-3 rounded-xl border border-red-300 bg-red-50 px-3 py-3 text-sm">
                <p className="font-medium text-red-900">
                  Con el cierre final no se abren más turnos hoy.
                </p>
                <p className="text-red-800 mt-1">
                  Si todavía queda gente por trabajar en el día, elegí{' '}
                  <strong>Cierre Parcial</strong>: cierra el turno y deja abrir el
                  siguiente.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowCloseModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleRequestClose}
              disabled={!closingCash}
            >
              <Lock className="h-4 w-4 mr-2" />
              Continuar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Confirmación de Cierre */}
      <Modal
        isOpen={showConfirmCloseModal}
        onClose={handleCancelConfirm}
        title={closeType === 'partial'
          ? `Confirmar Cierre de Turno${currentRegister?.shift_number ? ` ${currentRegister.shift_number}` : ''}`
          : 'Confirmar Cierre Final del Día'}
      >
        <div className="space-y-4">
          <div className={clsx(
            'p-4 border rounded-xl',
            closeType === 'partial'
              ? 'bg-amber-50 border-amber-200'
              : 'bg-red-50 border-red-200'
          )}>
            <div className="flex items-start gap-3">
              {closeType === 'partial' ? (
                <Clock className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={clsx(
                  'font-medium',
                  closeType === 'partial' ? 'text-amber-800' : 'text-red-800'
                )}>
                  {closeType === 'partial'
                    ? '¿Confirmar cierre de turno?'
                    : '¿Confirmar cierre final del día?'}
                </p>
                <p className={clsx(
                  'text-sm mt-1',
                  closeType === 'partial' ? 'text-amber-700' : 'text-red-700'
                )}>
                  {closeType === 'partial'
                    ? 'Podrá abrir otro turno después de este cierre.'
                    : 'No podrá abrir más turnos hoy después de este cierre.'}
                </p>
              </div>
            </div>
          </div>

          {/* Resumen final */}
          {currentRegister && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Efectivo Inicial:</span>
                <span className="font-medium">{formatPrice(currentRegister.opening_cash)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Ventas en Efectivo:</span>
                <span className="font-medium text-green-600">+{formatPrice(currentRegister.total_cash_sales)}</span>
              </div>
              {totalWithdrawals > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Salidas de Caja:</span>
                  <span className="font-medium text-red-600">-{formatPrice(totalWithdrawals)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm border-t pt-2">
                <span className="text-gray-600">Efectivo Esperado:</span>
                <span className="font-bold">{formatPrice(expectedCash)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Efectivo Declarado:</span>
                <span className="font-bold">{formatPrice(closingCashNum || 0)}</span>
              </div>
              {closingCash && closingCashNum !== expectedCash && (
                <div className={clsx(
                  'flex justify-between text-sm pt-2 border-t',
                  closingCashNum > expectedCash ? 'text-blue-600' : 'text-red-600'
                )}>
                  <span>Diferencia:</span>
                  <span className="font-bold">
                    {closingCashNum > expectedCash ? '+' : ''}
                    {formatPrice(closingCashNum - expectedCash)}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={handleCancelConfirm}>
              Volver
            </Button>
            <Button
              onClick={handleCloseRegister}
              loading={closeRegister.isPending}
              className={closeType === 'partial' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {closeType === 'partial' ? (
                <Clock className="h-4 w-4 mr-2" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              {closeType === 'partial' ? 'Cerrar Turno' : 'Cierre Final'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Registrar Salida */}
      <Modal
        isOpen={showWithdrawalModal}
        onClose={() => setShowWithdrawalModal(false)}
        title="Registrar Salida de Caja"
      >
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 rounded-xl">
            <p className="text-sm text-amber-800">
              Registre los gastos o retiros de efectivo realizados durante el día.
              Esto se descontará del efectivo esperado al cierre.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Monto
            </label>
            <CurrencyInput
              value={withdrawalAmount}
              onChange={setWithdrawalAmount}
              className="py-3 text-base"
            />
          </div>

          <Select
            label="Categoría"
            value={withdrawalCategory}
            onChange={(e) => {
              const val = e.target.value as CashWithdrawalCategory;
              setWithdrawalCategory(val);
              if (val !== 'salary') setWithdrawalRecipient('');
            }}
            options={[
              { value: 'delivery', label: 'Cadete/Envío' },
              { value: 'transport', label: 'Transporte (Uber, taxi, etc.)' },
              { value: 'supplies', label: 'Compras/Insumos' },
              { value: 'services', label: 'Servicios' },
              { value: 'salary', label: 'Sueldo' },
              { value: 'withdrawal', label: 'Retiro de Dinero' },
              { value: 'other', label: 'Otro' },
            ]}
          />

          {withdrawalCategory === 'salary' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Sueldo
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['Almacén', 'Peluquería'].map((tipo) => (
                  <button
                    key={tipo}
                    type="button"
                    onClick={() => setWithdrawalRecipient(tipo)}
                    className={clsx(
                      'flex items-center justify-center p-3 rounded-xl border-2 transition-all text-sm font-medium',
                      withdrawalRecipient === tipo
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    )}
                  >
                    {tipo}
                  </button>
                ))}
              </div>
              <Input
                label=""
                value={!['Almacén', 'Peluquería'].includes(withdrawalRecipient) ? withdrawalRecipient : ''}
                onChange={(e) => setWithdrawalRecipient(e.target.value)}
                placeholder="Otro (escribir nombre)"
                className="mt-2"
              />
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Método de Pago
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setWithdrawalPaymentMethod('cash')}
                className={clsx(
                  'flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all',
                  withdrawalPaymentMethod === 'cash'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                )}
              >
                <Banknote className="h-5 w-5 mb-1" />
                <span className="text-xs font-medium">Efectivo</span>
              </button>
              <button
                type="button"
                onClick={() => setWithdrawalPaymentMethod('card')}
                className={clsx(
                  'flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all',
                  withdrawalPaymentMethod === 'card'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                )}
              >
                <CreditCard className="h-5 w-5 mb-1" />
                <span className="text-xs font-medium">Tarjeta</span>
              </button>
              <button
                type="button"
                onClick={() => setWithdrawalPaymentMethod('transfer')}
                className={clsx(
                  'flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all',
                  withdrawalPaymentMethod === 'transfer'
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                )}
              >
                <ArrowRightLeft className="h-5 w-5 mb-1" />
                <span className="text-xs font-medium">Transferencia</span>
              </button>
            </div>
          </div>

          <Input
            label="Descripción (opcional)"
            value={withdrawalDescription}
            onChange={(e) => setWithdrawalDescription(e.target.value)}
            placeholder="Ej: Pago cadete envío zona sur"
          />

          <Input
            label="Destinatario (opcional)"
            value={withdrawalRecipient}
            onChange={(e) => setWithdrawalRecipient(e.target.value)}
            placeholder="Ej: Juan Pérez"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowWithdrawalModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateWithdrawal}
              loading={createWithdrawal.isPending}
              disabled={!withdrawalAmount}
            >
              <Plus className="h-4 w-4 mr-2" />
              Registrar Salida
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Historial */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title="Historial de Cajas"
        size="full"
      >
        <div className="overflow-auto">
          {history && history.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Turno</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo Cierre</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Apertura</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cierre</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ventas</th>
                  {canViewFinance && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ganancia</th>}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Diferencia</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.map((register) => (
                  <tr key={register.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {formatDate(register.opened_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={clsx(
                        'inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold',
                        register.shift_number === 1
                          ? 'bg-blue-100 text-blue-700'
                          : register.shift_number === 2
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-indigo-100 text-indigo-700'
                      )}>
                        {register.shift_number || 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className={clsx(
                          'px-2 py-1 rounded-full text-xs font-medium',
                          register.status === 'open'
                            ? 'bg-green-100 text-green-700'
                            : register.auto_closed
                            ? 'bg-amber-100 text-amber-700'
                            : register.close_type === 'partial'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-700'
                        )}>
                          {register.status === 'open'
                            ? 'Abierta'
                            : register.auto_closed
                            ? 'Auto'
                            : register.close_type === 'partial'
                            ? 'Parcial'
                            : 'Final'}
                        </span>
                        {register.auto_closed && (
                          <span title="Cierre automático">
                            <Clock className="h-3 w-3 text-amber-600" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatPrice(register.opening_cash)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {register.closing_cash ? formatPrice(register.closing_cash) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatPrice(register.total_sales)}
                    </td>
                    {canViewFinance && (
                      <td className={clsx(
                        'px-4 py-3 text-sm font-medium text-right',
                        register.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'
                      )}>
                        {formatPrice(register.total_profit)}
                      </td>
                    )}
                    <td className={clsx(
                      'px-4 py-3 text-sm text-right',
                      register.cash_difference == null
                        ? 'text-gray-400'
                        : register.cash_difference === 0
                        ? 'text-gray-500'
                        : register.cash_difference > 0
                        ? 'text-blue-600'
                        : 'text-red-600'
                    )}>
                      {register.cash_difference != null
                        ? `${register.cash_difference > 0 ? '+' : ''}${formatPrice(register.cash_difference)}`
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setViewRegisterId(register.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                        title="Ver detalle"
                      >
                        <Eye className="h-3 w-3" />
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No hay registros de caja anteriores
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Confirmar Eliminación de Salida */}
      <Modal
        isOpen={deleteWithdrawalId !== null}
        onClose={() => setDeleteWithdrawalId(null)}
        title="Eliminar Salida"
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-800">¿Eliminar esta salida de caja?</p>
                <p className="text-sm text-red-600 mt-1">
                  Esta acción no se puede deshacer. El monto será sumado nuevamente al efectivo esperado.
                </p>
              </div>
            </div>
          </div>

          {deleteWithdrawalId && (() => {
            const withdrawal = withdrawals.find(w => w.id === deleteWithdrawalId);
            if (!withdrawal) return null;
            const Icon = CATEGORY_ICONS[withdrawal.category] || MoreHorizontal;
            return (
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {withdrawal.description || WITHDRAWAL_CATEGORY_LABELS[withdrawal.category]}
                    </p>
                    <p className="text-sm text-gray-500">
                      {WITHDRAWAL_CATEGORY_LABELS[withdrawal.category]}
                      {withdrawal.recipient && ` • ${withdrawal.recipient}`}
                    </p>
                  </div>
                  <p className="text-xl font-bold text-red-600">
                    -{formatPrice(withdrawal.amount)}
                  </p>
                </div>
              </div>
            );
          })()}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setDeleteWithdrawalId(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteWithdrawal}
              loading={deleteWithdrawal.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Confirmar Reapertura de Caja */}
      <Modal
        isOpen={showReopenModal}
        onClose={() => {
          setShowReopenModal(false);
          setReopenRegisterId(null);
        }}
        title="Reabrir Caja"
      >
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RotateCcw className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-800">¿Reabrir esta caja?</p>
                <p className="text-sm text-amber-700 mt-1">
                  La caja se reabrirá y podrá continuar registrando ventas y salidas.
                  Los datos de cierre se eliminarán.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Solo el administrador puede reabrir cajas cerradas.
              Use esta función si la caja se cerró por error o necesita agregar transacciones.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowReopenModal(false);
                setReopenRegisterId(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReopenRegister}
              loading={reopenRegister.isPending}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reabrir Caja
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Ver Detalle de Caja */}
      <Modal
        isOpen={viewRegisterId !== null}
        onClose={() => setViewRegisterId(null)}
        title={`Detalle de Caja${viewRegisterData?.shift_number && viewRegisterData.shift_number > 1 ? ` - Turno ${viewRegisterData.shift_number}` : ''}`}
        size="full"
      >
        {viewRegisterData && (
          <div className="space-y-6">
            {/* Info de apertura y cierre */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-5 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 text-green-700 mb-3">
                  <Unlock className="h-5 w-5" />
                  <span className="font-medium">Apertura</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatDateTime(viewRegisterData.opened_at)}</p>
                {viewRegisterData.opened_by && (
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-2">
                    <User className="h-4 w-4" />
                    {viewRegisterData.opened_by.full_name}
                  </p>
                )}
                {viewRegisterData.opening_notes && (
                  <p className="text-sm text-gray-500 mt-3 italic">"{viewRegisterData.opening_notes}"</p>
                )}
              </div>

              <div className="p-5 bg-red-50 rounded-xl border border-red-200">
                <div className="flex items-center gap-2 text-red-700 mb-3">
                  <Lock className="h-5 w-5" />
                  <span className="font-medium">
                    Cierre {viewRegisterData.close_type === 'partial' ? 'Parcial' : 'Final'}
                    {viewRegisterData.auto_closed && ' (Auto)'}
                  </span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {viewRegisterData.closed_at ? formatDateTime(viewRegisterData.closed_at) : '-'}
                </p>
                {viewRegisterData.closed_by && (
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-2">
                    <User className="h-4 w-4" />
                    {viewRegisterData.closed_by.full_name}
                  </p>
                )}
                {viewRegisterData.closing_notes && (
                  <p className="text-sm text-gray-500 mt-3 italic">"{viewRegisterData.closing_notes}"</p>
                )}
              </div>
            </div>

            {/* Resumen financiero */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Efectivo Inicial</p>
                <p className="text-2xl font-bold text-gray-900">{formatPrice(viewRegisterData.opening_cash)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-blue-600 mb-2">Ventas Totales</p>
                <p className="text-2xl font-bold text-blue-700">{formatPrice(viewRegisterData.total_sales)}</p>
              </div>
              {canViewFinance && (
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                  <p className="text-sm text-emerald-600 mb-2">Ganancia</p>
                  <p className={`text-2xl font-bold ${(viewRegisterData.total_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatPrice(viewRegisterData.total_profit)}</p>
                  {(viewRegisterData.total_expenses || 0) > 0 && (
                    <p className="text-xs text-gray-500 mt-1">Gastos: -{formatPrice(viewRegisterData.total_expenses)}</p>
                  )}
                </div>
              )}
              <div className={clsx(
                'rounded-xl p-4 border',
                viewRegisterData.cash_difference === 0
                  ? 'bg-gray-50 border-gray-200'
                  : (viewRegisterData.cash_difference || 0) > 0
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-red-50 border-red-200'
              )}>
                <p className="text-sm text-gray-500 mb-2">Diferencia</p>
                <p className={clsx(
                  'text-2xl font-bold',
                  viewRegisterData.cash_difference === 0
                    ? 'text-gray-700'
                    : (viewRegisterData.cash_difference || 0) > 0
                    ? 'text-blue-700'
                    : 'text-red-700'
                )}>
                  {(viewRegisterData.cash_difference || 0) > 0 && '+'}
                  {formatPrice(viewRegisterData.cash_difference || 0)}
                </p>
              </div>
            </div>

            {/* Desglose por método de pago */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h4 className="font-medium text-gray-700 mb-4">Desglose de Ventas</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Banknote className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-500">Efectivo</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{formatPrice(viewRegisterData.total_cash_sales)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-500">Tarjeta</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{formatPrice(viewRegisterData.total_card_sales)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowRightLeft className="h-5 w-5 text-purple-600" />
                    <span className="text-sm text-gray-500">Transferencia</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{formatPrice(viewRegisterData.total_transfer_sales)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <QrCode className="h-5 w-5 text-orange-600" />
                    <span className="text-sm text-gray-500">QR</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{formatPrice(viewRegisterData.total_qr_sales)}</p>
                </div>
              </div>
            </div>

            {/* Cálculo de caja */}
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
              <h4 className="font-medium text-amber-700 mb-4">Cálculo de Caja</h4>
              <div className="space-y-3">
                <div className="flex justify-between text-base">
                  <span className="text-gray-600">
                    Efectivo inicial
                    <span className="block text-xs text-gray-400">Lo que había en el cajón al abrir</span>
                  </span>
                  <span className="font-medium">{formatPrice(viewRegisterData.opening_cash)}</span>
                </div>
                <div className="flex justify-between text-base text-green-700">
                  <span>
                    + Ventas en efectivo
                    <span className="block text-xs text-green-600/70">Lo que entró cobrando en efectivo</span>
                  </span>
                  <span className="font-medium">+{formatPrice(viewRegisterData.total_cash_sales)}</span>
                </div>
                {/* Salidas en efectivo.
                    `total_cash_withdrawals` NO es una columna: se calcula en memoria y
                    solo para la caja abierta, asi que en una caja cerrada llega vacio y
                    esta linea desaparecia. El cierre quedaba mostrando
                    "inicial + ventas = esperado" con un resultado que no cierra, y daba
                    la impresion de que los gastos no se descontaban (si se descontaban:
                    el esperado ya los tenia restados, solo faltaba mostrarlos).
                    Cuando no viene, se reconstruye exacto despejando del esperado. */}
                {(() => {
                  const salidas =
                    viewRegisterData.total_cash_withdrawals != null
                      ? Number(viewRegisterData.total_cash_withdrawals)
                      : Number(viewRegisterData.opening_cash || 0) +
                        Number(viewRegisterData.total_cash_sales || 0) -
                        Number(viewRegisterData.expected_cash || 0);
                  if (salidas <= 0) return null;
                  // De que se componen, para no tener que ir a buscarlo a los bloques
                  // de abajo y sumarlo a mano.
                  const enEfectivo = viewRegisterWithdrawals.filter((w) => w.payment_method === 'cash');
                  const suma = (arr: typeof enEfectivo) =>
                    arr.reduce((acc, w) => acc + Number(w.amount || 0), 0);
                  const gastos = suma(enEfectivo.filter((w) => !['withdrawal', 'supplier_payment'].includes(w.category)));
                  const retiros = suma(enEfectivo.filter((w) => ['withdrawal', 'supplier_payment'].includes(w.category)));
                  return (
                    <div className="text-base text-red-700">
                      <div className="flex justify-between">
                        <span>
                          - Salidas de efectivo
                          <span className="block text-xs text-red-600/70">Todo lo que se sacó del cajón</span>
                        </span>
                        <span className="font-medium">-{formatPrice(salidas)}</span>
                      </div>
                      {(gastos > 0 || retiros > 0) && (
                        <div className="mt-1 ml-4 space-y-0.5 text-xs text-red-600/80">
                          {gastos > 0 && (
                            <div className="flex justify-between">
                              <span>Gastos (limpieza, delivery, servicios…)</span>
                              <span>-{formatPrice(gastos)}</span>
                            </div>
                          )}
                          {retiros > 0 && (
                            <div className="flex justify-between">
                              <span>Retiros y pagos a proveedores</span>
                              <span>-{formatPrice(retiros)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="flex justify-between border-t border-amber-300 pt-3 text-base">
                  <span className="font-medium text-gray-700">
                    = Efectivo esperado
                    <span className="block text-xs font-normal text-gray-400">
                      Lo que tendría que haber en el cajón
                    </span>
                  </span>
                  <span className="font-bold text-lg">{formatPrice(viewRegisterData.expected_cash || 0)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-600">
                    Efectivo declarado
                    <span className="block text-xs text-gray-400">Lo que se contó al cerrar</span>
                  </span>
                  <span className="font-medium">{formatPrice(viewRegisterData.closing_cash || 0)}</span>
                </div>

                {/* El resultado del arqueo, en castellano. Es el dato que importa y
                    hasta ahora habia que sacarlo restando dos numeros de arriba. */}
                {(() => {
                  const dif = Number(viewRegisterData.closing_cash || 0) - Number(viewRegisterData.expected_cash || 0);
                  if (Math.abs(dif) < 0.01) {
                    return (
                      <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
                        <Check className="h-4 w-4 flex-shrink-0" />
                        <span>La caja cerró justo: lo contado coincide con lo esperado.</span>
                      </div>
                    );
                  }
                  const falta = dif < 0;
                  return (
                    <div className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm border ${
                      falta
                        ? 'bg-red-50 border-red-200 text-red-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        {falta ? 'Faltan ' : 'Sobran '}
                        <strong>{formatPrice(Math.abs(dif))}</strong>
                        {falta
                          ? ' — se contó menos de lo que la cuenta esperaba.'
                          : ' — se contó más de lo que la cuenta esperaba.'}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Gastos (afectan ganancia) */}
            {viewRegisterWithdrawals.filter((w) => w.category !== 'withdrawal' && w.category !== 'supplier_payment').length > 0 && (
              <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-red-700 flex items-center gap-2">
                    <MinusCircle className="h-5 w-5" />
                    Gastos
                  </h4>
                  <span className="text-lg font-bold text-red-700">-{formatPrice(viewRegisterData.total_expenses || 0)}</span>
                </div>
                <p className="text-xs text-red-600 mb-4">Restan de la ganancia</p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {viewRegisterWithdrawals.filter((w) => w.category !== 'withdrawal' && w.category !== 'supplier_payment').map((w) => {
                    const Icon = CATEGORY_ICONS[w.category] || MoreHorizontal;
                    return (
                      <div key={w.id} className="flex items-center justify-between bg-white rounded-xl p-3 border border-red-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                            <Icon className="h-5 w-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {w.description || WITHDRAWAL_CATEGORY_LABELS[w.category]}
                            </p>
                            <p className="text-sm text-gray-500">
                              {PAYMENT_METHOD_LABELS[w.payment_method]}
                              {w.recipient && ` • ${w.recipient}`}
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-medium text-red-600">-{formatPrice(w.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pagos a Proveedores (NO afectan ganancia) */}
            {viewRegisterWithdrawals.filter((w) => w.category === 'supplier_payment').length > 0 && (
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-amber-700 flex items-center gap-2">
                    <Store className="h-5 w-5" />
                    Pagos a Proveedores
                  </h4>
                  <span className="text-lg font-bold text-amber-700">
                    -{formatPrice(viewRegisterWithdrawals.filter((w) => w.category === 'supplier_payment').reduce((sum, w) => sum + Number(w.amount), 0))}
                  </span>
                </div>
                <p className="text-xs text-amber-600 mb-4">Reducen saldo de caja, no afectan la ganancia</p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {viewRegisterWithdrawals.filter((w) => w.category === 'supplier_payment').map((w) => (
                    <div key={w.id} className="flex items-center justify-between bg-white rounded-xl p-3 border border-amber-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                          <Store className="h-5 w-5 text-amber-700" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {w.description || WITHDRAWAL_CATEGORY_LABELS[w.category]}
                          </p>
                          <p className="text-sm text-gray-500">
                            {PAYMENT_METHOD_LABELS[w.payment_method]}
                            {w.recipient && ` • ${w.recipient}`}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-medium text-amber-700">-{formatPrice(w.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Retiros (NO afectan ganancia) */}
            {viewRegisterWithdrawals.filter((w) => w.category === 'withdrawal').length > 0 && (
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-blue-700 flex items-center gap-2">
                    <Banknote className="h-5 w-5" />
                    Retiros
                  </h4>
                  <span className="text-lg font-bold text-blue-700">
                    -{formatPrice(viewRegisterWithdrawals.filter((w) => w.category === 'withdrawal').reduce((sum, w) => sum + Number(w.amount), 0))}
                  </span>
                </div>
                <p className="text-xs text-blue-600 mb-4">No afectan la ganancia</p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {viewRegisterWithdrawals.filter((w) => w.category === 'withdrawal').map((w) => {
                    const Icon = CATEGORY_ICONS[w.category] || MoreHorizontal;
                    return (
                      <div key={w.id} className="flex items-center justify-between bg-white rounded-xl p-3 border border-blue-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                            <Icon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {w.description || WITHDRAWAL_CATEGORY_LABELS[w.category]}
                            </p>
                            <p className="text-sm text-gray-500">
                              {PAYMENT_METHOD_LABELS[w.payment_method]}
                              {w.recipient && ` • ${w.recipient}`}
                            </p>
                          </div>
                        </div>
                        <span className="text-lg font-medium text-blue-600">-{formatPrice(w.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={() => setViewRegisterId(null)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
